import { useApi } from '@gravitate-js/the-armory'
import { getAPIURL } from '@utils/api'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ManagementOverviewResponse, OverviewResponse } from './response'
import type { DeleteRequest, UpsertDriverCredentialRequest } from './request'
import { NotificationMessage } from '@gravitate-js/excalibrr'

export const endpoints = {
  driverCredential: {
    overview: 'driver_credential/overview',
    upsert: 'driver_credential/upsert',
  },
  credential: {
    overview: 'credential_management/overview',
    delete: 'credential_management/delete',
    upsert: 'credential_management/upsert',
  },
}

export const useDriverCredentials = () => {
  const api = useApi({ baseURLOverride: getAPIURL() })
  const queryClient = useQueryClient()

  const getDriverCredentialsOverviewQuery = () =>
    useQuery({
      queryKey: [endpoints.driverCredential.overview],
      queryFn: () => api.post(endpoints.driverCredential.overview) as OverviewResponse[],
    })

  const editDriverCredentialMutation = useMutation({
    mutationKey: [endpoints.driverCredential.upsert],
    mutationFn: (request: UpsertDriverCredentialRequest) => api.post(endpoints.driverCredential.upsert, request),
    onMutate: async (request) => {
      const oldData = queryClient.getQueryData<OverviewResponse[]>([endpoints.driverCredential.overview])

      return { oldData }
    },
    onError: async (_error, _request, context) => {
      queryClient.setQueriesData({ queryKey: [endpoints.driverCredential.overview] }, context.oldData)
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: [endpoints.driverCredential.overview],
      })
    },
  })

  const getCredentialsOverviewQuery = () =>
    useQuery({
      queryKey: [endpoints.credential.overview],
      queryFn: () => api.post(endpoints.credential.overview) as { rows: ManagementOverviewResponse[] },
    })

  const deleteCredentialMutation = useMutation({
    mutationKey: [endpoints.credential.delete],
    mutationFn: (request: DeleteRequest) => api.post(endpoints.credential.delete, { credential_id: request }),
    onMutate: async (request) => {
      const oldData = queryClient.getQueryData<{ rows: ManagementOverviewResponse[] }>([endpoints.credential.overview])
      queryClient.setQueriesData<{ rows: ManagementOverviewResponse[] }>(
        { queryKey: [endpoints.credential.overview] },
        (oldData) => {
          return { rows: oldData?.rows?.filter((row) => row.credential_id !== request) }
        }
      )
      return { oldData }
    },
    onError: async (_error, _request, context) => {
      queryClient.setQueriesData({ queryKey: [endpoints.credential.overview] }, context.oldData)
    },
    onSettled: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: [endpoints.driverCredential.overview],
      })
    },
  })

  const upsertCredentialsMutation = useMutation({
    mutationKey: [endpoints.credential.upsert],
    mutationFn: (request: UpsertDriverCredentialRequest) => api.post(endpoints.credential.upsert, request),
    onSuccess: async (data) => {
      NotificationMessage('Saved', 'Your changes have been saved!', false)
      queryClient.setQueriesData({ queryKey: [endpoints.credential.overview] }, data)
    },
    onError: async (_error, _request, context) => {
      await queryClient.invalidateQueries({
        queryKey: [endpoints.credential.overview],
      })
    },
    onSettled: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: [endpoints.driverCredential.overview],
      })
    },
  })

  return {
    getDriverCredentialsOverviewQuery,
    editDriverCredentialMutation,
    getCredentialsOverviewQuery,
    deleteCredentialMutation,
    upsertCredentialsMutation,
  }
}
